import IconChevronDown from '@apollo/icons/small/IconChevronDown.svg';
import IconError from '@apollo/icons/small/IconError.svg';
import IconOverflow from '@apollo/icons/small/IconOverflow.svg';
import IconSingleService from '@apollo/icons/small/IconSingleService.svg';
import IconWarning from '@apollo/icons/small/IconWarning.svg';
import {
  Button,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Tooltip,
} from '@apollo/orbit';
import classnames from 'classnames';
import React from 'react';

import { TraceNodeKind } from 'src/lib/graphqlTypes/types';

import { TraceTreeNode } from '../TraceTree';

import { TraceNodeAttributes } from './TraceNodeAttributes';
import { TraceNodeName } from './TraceNodeName';

export const TraceNodeHandle = ({
  node,
  isOpen,
  hovering = false,
  showErrorSignifier,
}: {
  node: TraceTreeNode;
  isOpen: boolean;
  hovering?: boolean;
  showErrorSignifier?: boolean;
}) => {
  const hasChildren = node.children.length > 0;
  const hasErrors = node.errors.length > 0;
  const canOpen = hasChildren || hasErrors;
  const warningMessages = [];
  if (node.showClockSkewWarning) {
    warningMessages.push(
      'The trace timings reported by this subgraph are out of sync with the router. The spans below have been adjusted for display purposes, but to avoid this in the future, ensure that your server clocks are synchronized to the same times.',
    );
  }

  if (node.childrenAreTruncated) {
    if (
      node.childCount !== undefined &&
      node.children.length < node.childCount
    ) {
      warningMessages.push(
        `This node contains ${node.childCount} children, only the first ${
          node.children.length
        } ${node.children.length === 1 ? 'is' : 'are'} shown.`,
      );
    } else {
      warningMessages.push(
        `This node contains too many children and it has been truncated.`,
      );
    }
  }

  if (node.isRootNode && node.isIncomplete) {
    warningMessages.push(
      'This trace was sent through as potentially incomplete, which means that some trace nodes might be missing below.',
    );
  }

  return (
    <span className="-ml-7 mr-1 inline-flex items-center overflow-hidden">
      <Popover placement="bottom-end">
        <PopoverTrigger>
          <Button
            className={classnames(
              'h-5 w-7 min-w-0 flex-none p-0 leading-none',
              {
                invisible: !hovering || node.attributes.length === 0,
              },
            )}
            onClick={(e) => {
              e.stopPropagation();
            }}
            variant="secondary"
            size="sm"
            leftIcon={<IconOverflow />}
          />
        </PopoverTrigger>
        <div className="z-50">
          <PopoverContent className="w-full min-w-[20vw] max-w-[40vw]">
            <PopoverHeader className="text-title">
              <div className="flex min-w-0">
                <span className="truncate">Span Details</span>
                <span className="ml-2 truncate rounded bg-info px-2 pb-0.5 text-info">
                  {node.key}
                </span>
              </div>
              <PopoverCloseButton />
            </PopoverHeader>
            <PopoverBody className="p-3">
              <div className="overflow-hidden">
                <TraceNodeAttributes node={node} />
              </div>
            </PopoverBody>
          </PopoverContent>
        </div>
      </Popover>

      <span className="z-10 inline-flex items-center overflow-hidden py-1 font-mono">
        <span
          title={`${!node.isDeferredFetch ? node.name : ``}${
            node.type && !node.isDeferredFetch ? `: ${node.type}` : ''
          }`}
          className={classnames(
            'ml-1 inline-flex items-center overflow-hidden truncate rounded-sm leading-none',
            {
              'border border-primary px-2': canOpen,
            },
            hasErrors
              ? 'bg-error-hover text-inverted'
              : hasChildren
                ? 'bg-secondary text-primary'
                : 'text-primary',
          )}
        >
          {canOpen && (
            <IconChevronDown
              className={classnames(
                'mr-1 flex-none duration-150',
                {
                  '-rotate-90': !isOpen,
                },
                hasErrors ? 'text-inverted' : 'text-secondary',
              )}
            />
          )}
          {hasErrors && <IconError className="ml-1 mr-2 flex-none" />}
          <TraceNodeName node={node} />
        </span>
        {showErrorSignifier && !hasErrors && (
          <span className="ml-2 size-2 flex-shrink-0 rounded bg-error-hover" />
        )}
        {node.kind === TraceNodeKind.ROUTER_INTERNAL && node.subgraphName && (
          <>
            <span className="ml-2 mr-1">→</span>
            <IconSingleService className="mx-1 flex-none text-placeholder" />
            <span className="truncate font-semibold text-secondary">
              {node.subgraphName}
            </span>
          </>
        )}
        {warningMessages.length > 0 && (
          <Tooltip label={warningMessages.join(' ')}>
            <IconWarning className="ml-1 flex-none text-icon-warning" />
          </Tooltip>
        )}
      </span>
    </span>
  );
};
